import { type FC, useEffect, useMemo, useState } from "react";
import {
  lineChartFactory,
  noData,
  validLineData,
  formattedAverageTime,
  chartColorScale,
  type QueryFilter,
  lineChartTitles,
  chartLegendLabels,
  totalActivity,
  dailyActivity,
  starts,
  completes,
  createDateRange,
  fallbackToZero,
} from "features/Library/Common/utils/performanceUtils";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import { reportingColors } from "features/Library/Common/utils/constants";
import { ChartWrapper, HorizontalBarChart, LineChart, getDateFormatByCount } from "components/charts";
import CardReporting, { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type ColumnOption } from "interfaces/columnOptions";
import { GenericPerformanceList } from "../../shared/GenericPerformanceList";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { type AssessmentDetails, type AssessmentEngagement } from "features/Reporting/types/content";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { useQuery } from "@tanstack/react-query";
import * as contentReportingService from "features/Reporting/services/contentReportingService";
import dateTimeUtils from "utils/dateTimeUtils";
import { type CommonAccountReportPropsExtended } from "../../types";
import { EntityType } from "features/Library/Flows/Designer/types";
import { sharedAccountReportingHorizontalBarProps, sharedAccountReportingLineProps } from "../../shared";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { type AppDispatch } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";
import { type ConnectedProps, connect } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { doAssessmentExport } from "features/Reporting/state/content/contentActions";

import "features/Library/Common/utils/performanceSCSSUtils.scss";
import styles from "../../contentOverviewReports.module.scss";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";

const getCardsReportData = (cardData: AssessmentEngagement): CardReportingItem[] => {
  return [
    {
      statDescription: "Accounts",
      popupBody: "The total number of customers across all assessments.",
      stat: fallbackToZero(cardData.Customers),
    },
    {
      statDescription: completes,
      popupBody: "Total assessment completes across all assessments in your account.",
      stat: fallbackToZero(cardData.Completed),
    },
    {
      statDescription: "Completion Rate",
      popupBody: "The percentage of assessments completed compared to the total number of assessments started.",
      stat: fallbackToZero(cardData.CompletionRate) * 100,
      isStatPercentage: true,
    },
    {
      statDescription: "Avg. Completion Time",
      popupBody: "The average completion time across all assessments.",
      stat: formattedAverageTime(fallbackToZero(cardData.AverageTime)),
    },
    {
      statDescription: "Average Grade",
      popupBody: "The average user grade across all assessments.",
      stat: fallbackToZero(cardData.AverageGrade) * 100,
      isStatPercentage: true,
    },
  ];
};

export type Props = CommonAccountReportPropsExtended & PropsFromRedux;

const fetchDailyData = async ({ queryKey }: QueryFilter) => {
  const result = (await contentReportingService.getAssessmentActivity(queryKey[1])).data;
  return lineChartFactory(result);
};

const fetchEngagementQuery = async ({ queryKey }: QueryFilter) => {
  const { data } = await contentReportingService.getAssessmentEngagement(queryKey[1]);

  return {
    barData: [
      {
        id: starts.toLowerCase(),
        category: starts,
        value: data.Started ?? 0,
        fillColor: reportingColors.blue,
      },
      {
        id: completes.toLowerCase(),
        category: completes,
        value: data.Completed ?? 0,
        fillColor: reportingColors.yellow,
      },
    ],
    carData: {
      ...data,
    },
  };
};

const fetchDetailsQuery = async ({ queryKey }: QueryFilter) => {
  const result = (await contentReportingService.getAssessmentDetails(queryKey[1])).data;
  return result.map((item, index) => {
    const roundedCompletionRate = roundToTwoDigits(item.CompletionRate as number);
    return {
      ...item,
      CompletionRate: roundedCompletionRate,
      id: index,
    };
  });
};

const barDataValid = (...valueArr: number[]) => valueArr.every((value) => value !== 0);

export const AssessmentReport: FC<Props> = ({
  filter,
  setSelectedContent,
  setHasExportData,
  setExportAction,
  assessmentExport,
  setIsReportEnabled,
  resetIsReportEnabled,
}) => {
  const [chartState, setChartState] = useState(totalActivity);
  const reportEnabled = useFeatureFlag(FeatureFlags.AssessmentPerformanceOverview);

  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useQuery({
    queryKey: ["AssessmentReportDailyData", { ...filter, aggregation: chartPeriod }],
    queryFn: fetchDailyData,
    enabled: reportEnabled && chartPeriod !== CHART_PERIOD.UNSET && filter.type !== undefined,
  });

  const engagementQuery = useQuery({
    queryKey: ["AssessmentReportEngagement", filter],
    queryFn: fetchEngagementQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

  const detailsQuery = useQuery({
    queryKey: ["AssessmentReportDetails", filter],
    queryFn: fetchDetailsQuery,
    enabled: reportEnabled && filter.type !== undefined,
  });

  useEffect(() => {
    setHasExportData({
      hasDataToExport: !!detailsQuery.data && detailsQuery.data.length > 0,
      tooltipContent: "No data available for export. There are no assessments with activity data.",
    });
  }, [detailsQuery.data, setHasExportData]);

  // Setting up correct export method if send entity is changed
  useEffect(() => {
    setExportAction({
      method: assessmentExport,
      args: [filter],
      isExportEnabled: true,
    });
  }, [assessmentExport, filter, setExportAction]);

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, setIsReportEnabled, resetIsReportEnabled]);

  const lineData = useMemo(() => {
    if (chartState === dailyActivity) {
      return {
        xData: [lineChartQuery.data?.Date, lineChartQuery.data?.Date],
        yData: [lineChartQuery.data?.Started, lineChartQuery.data?.Completed],
      };
    }
    return {
      xData: [lineChartQuery.data?.Date, lineChartQuery.data?.Date],
      yData: [lineChartQuery.data?.CumulativeStarted, lineChartQuery.data?.CumulativeCompleted],
    };
  }, [chartState, lineChartQuery.data]);

  const cardData = useMemo(() => {
    if (engagementQuery.isSuccess) {
      const cards = getCardsReportData(engagementQuery.data.carData);
      return filter.showCustomers ? cards : cards.slice(1);
    }
  }, [engagementQuery.data, engagementQuery.isSuccess, filter.showCustomers]);

  const columns: ColumnOption<AssessmentDetails>[] = useMemo(
    () => [
      {
        name: "Assessment",
        width: 7,
        isSortable: false,
        render(item) {
          return (
            <Tooltip
              target={
                filter.showCustomers ? (
                  <TextTruncate lines={2} className={styles.wordBreak}>
                    {item.AssessmentTitle}
                  </TextTruncate>
                ) : (
                  <button
                    className={styles.linkButton}
                    onClick={() =>
                      setSelectedContent({
                        id: item.AssessmentId,
                        type: EntityType.Assessment,
                        name: item.AssessmentTitle,
                      })
                    }
                  >
                    <TextTruncate lines={2} className={styles.wordBreak}>
                      {item.AssessmentTitle}
                    </TextTruncate>
                  </button>
                )
              }
              content={item.AssessmentTitle}
              showAlways
            />
          );
        },
      },
      {
        width: 4,
        name: `Total ${starts}`,
        isSortable: false,
        render(item) {
          return <div>{item.TotalStarts.toLocaleString()}</div>;
        },
      },
      {
        width: 4,
        isSortable: false,
        name: `Total ${completes}`,
        render(item) {
          return <div>{item.TotalCompletes.toLocaleString()}</div>;
        },
      },
      {
        name: "Completion Rate",
        isSortable: false,
        width: 4,
        render(item) {
          return <div>{roundToTwoDigits((item.CompletionRate as number) * 100)}%</div>;
        },
      },
      {
        isSortable: false,
        name: "Avg. Time",
        width: 4,
        render(item) {
          return <div>{formattedAverageTime(item.AverageTime)}</div>;
        },
      },
      {
        width: 4,
        name: "Avg. Grade",
        isSortable: false,
        render(item) {
          return (
            <div>
              {item.AverageGrade !== null && item.AverageGrade !== undefined
                ? `${roundToTwoDigits(item.AverageGrade * 100)}%`
                : "-"}
            </div>
          );
        },
      },
      {
        isSortable: false,
        width: 4,
        name: "Last Activity",
        render(item) {
          return <div>{dateTimeUtils.formatDate(item.LastActivity)}</div>;
        },
      },
    ],
    [filter.showCustomers, setSelectedContent],
  );

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className={styles.reportBody}>
      <div className={styles.lineChartContainer} ref={measureRef}>
        <ChartWrapper
          titles={lineChartTitles}
          showLegend
          legendLabels={chartLegendLabels}
          colorRange={chartColorScale}
          onChange={setChartState}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess &&
            validLineData([
              lineChartQuery.data.Started,
              lineChartQuery.data.Completed,
              lineChartQuery.data.CumulativeStarted,
              lineChartQuery.data.CumulativeCompleted,
            ]) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                {...lineData}
                colorRange={chartColorScale}
                xFormatterFunc={getDateFormatByCount(lineChartQuery.data.Date.length)}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.funnelChartContainer}>
        <ChartWrapper titles={["Engagement"]}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess &&
            barDataValid(engagementQuery.data.barData[0].value, engagementQuery.data.barData[1].value) ? (
              <HorizontalBarChart {...sharedAccountReportingHorizontalBarProps} data={engagementQuery.data.barData} />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.performanceCardSection}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={cardData!} />}
        </RequestStatusRenderer>
      </div>
      <div className={styles.table}>
        <RequestStatusRenderer state={detailsQuery.status}>
          {detailsQuery.isSuccess && (
            <GenericPerformanceList title="Assessments" rows={detailsQuery.data} columns={columns} filter={filter} />
          )}
        </RequestStatusRenderer>
      </div>
    </div>
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  assessmentExport: bindActionCreators(doAssessmentExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
});

const connector = connect(null, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AssessmentReport);
export default ConnectedComponent;
